import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ContactDetailsTab = ({ formData, errors, onChange }) => {
  const [showAddPhone, setShowAddPhone] = useState(false);
  const [showAddEmail, setShowAddEmail] = useState(false);

  const phoneTypeOptions = [
    { value: 'mobile', label: 'Mobiltelefon' },
    { value: 'home', label: 'Festnetz (Privat)' },
    { value: 'work', label: 'Festnetz (Arbeit)' }
  ];

  const emailTypeOptions = [
    { value: 'personal', label: 'Privat' },
    { value: 'work', label: 'Geschäftlich' }
  ];

  const preferredContactOptions = [
    { value: 'phone', label: 'Telefon' },
    { value: 'email', label: 'E-Mail' },
    { value: 'post', label: 'Post' },
    { value: 'sms', label: 'SMS' }
  ];

  const handleAddPhone = () => {
    const newPhone = {
      id: Date.now(),
      type: 'mobile',
      number: '',
      isPrimary: formData?.phoneNumbers?.length === 0
    };
    onChange('phoneNumbers', [...formData?.phoneNumbers, newPhone]);
    setShowAddPhone(false);
  };

  const handleRemovePhone = (id) => {
    onChange('phoneNumbers', formData?.phoneNumbers?.filter(p => p?.id !== id));
  };

  const handlePhoneChange = (id, field, value) => {
    onChange('phoneNumbers', formData?.phoneNumbers?.map(p => 
      p?.id === id ? { ...p, [field]: value } : p
    ));
  };

  const handleAddEmail = () => {
    const newEmail = {
      id: Date.now(),
      type: 'personal',
      address: '',
      isPrimary: formData?.emailAddresses?.length === 0
    };
    onChange('emailAddresses', [...formData?.emailAddresses, newEmail]);
    setShowAddEmail(false);
  };

  const handleRemoveEmail = (id) => {
    onChange('emailAddresses', formData?.emailAddresses?.filter(e => e?.id !== id));
  };

  const handleEmailChange = (id, field, value) => {
    onChange('emailAddresses', formData?.emailAddresses?.map(e => 
      e?.id === id ? { ...e, [field]: value } : e
    ));
  };

  return (
    <div className="space-y-6">
      {/* Phone Numbers Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Phone" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Telefonnummern</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddPhone(true)}
          >
            Hinzufügen
          </Button>
        </div>

        <div className="space-y-4">
          {formData?.phoneNumbers?.map((phone, index) => (
            <div key={phone?.id} className="flex items-start gap-3 p-4 bg-background rounded-lg border border-border">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select
                  label={index === 0 ? "Typ" : ""}
                  options={phoneTypeOptions}
                  value={phone?.type}
                  onChange={(value) => handlePhoneChange(phone?.id, 'type', value)}
                  placeholder="Typ wählen"
                />
                
                <Input
                  label={index === 0 ? "Telefonnummer" : ""}
                  type="tel"
                  value={phone?.number}
                  onChange={(e) => handlePhoneChange(phone?.id, 'number', e?.target?.value)}
                  placeholder="+49 30 12345678"
                  pattern="[+]?[0-9\s-]+"
                />

                <div className="flex items-end gap-2">
                  <Checkbox
                    label="Primär"
                    checked={phone?.isPrimary}
                    onChange={(e) => handlePhoneChange(phone?.id, 'isPrimary', e?.target?.checked)}
                  />
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemovePhone(phone?.id)}
                className="mt-6"
              >
                <Icon name="Trash2" size={18} className="text-error" />
              </Button>
            </div>
          ))}

          {showAddPhone && (
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handleAddPhone}>
                Bestätigen
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddPhone(false)}>
                Abbrechen
              </Button>
            </div>
          )}

          {formData?.phoneNumbers?.length === 0 && !showAddPhone && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Keine Telefonnummern hinzugefügt
            </p>
          )}
        </div>
      </div>
      {/* Email Addresses Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Mail" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">E-Mail-Adressen</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddEmail(true)}
          >
            Hinzufügen
          </Button>
        </div>

        <div className="space-y-4">
          {formData?.emailAddresses?.map((email, index) => (
            <div key={email?.id} className="flex items-start gap-3 p-4 bg-background rounded-lg border border-border">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select
                  label={index === 0 ? "Typ" : ""}
                  options={emailTypeOptions}
                  value={email?.type}
                  onChange={(value) => handleEmailChange(email?.id, 'type', value)}
                  placeholder="Typ wählen"
                />
                
                <Input
                  label={index === 0 ? "E-Mail-Adresse" : ""}
                  type="email"
                  value={email?.address}
                  onChange={(e) => handleEmailChange(email?.id, 'address', e?.target?.value)}
                  placeholder="max.mustermann@beispiel.de"
                />

                <div className="flex items-end gap-2">
                  <Checkbox
                    label="Primär"
                    checked={email?.isPrimary}
                    onChange={(e) => handleEmailChange(email?.id, 'isPrimary', e?.target?.checked)}
                  />
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveEmail(email?.id)}
                className="mt-6"
              >
                <Icon name="Trash2" size={18} className="text-error" />
              </Button>
            </div>
          ))}

          {showAddEmail && (
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handleAddEmail}>
                Bestätigen
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddEmail(false)}>
                Abbrechen
              </Button>
            </div>
          )}

          {formData?.emailAddresses?.length === 0 && !showAddEmail && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Keine E-Mail-Adressen hinzugefügt
            </p>
          )}
        </div>
      </div>
      {/* Communication Preferences */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Kommunikationspräferenzen</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Bevorzugter Kontaktweg"
            required
            options={preferredContactOptions}
            value={formData?.preferredContact}
            onChange={(value) => onChange('preferredContact', value)}
            error={errors?.preferredContact}
            placeholder="Bitte wählen"
          />

          <Input
            label="Beste Erreichbarkeit (Uhrzeit)"
            type="text"
            value={formData?.bestTimeToContact}
            onChange={(e) => onChange('bestTimeToContact', e?.target?.value)}
            placeholder="z.B. 9:00 - 17:00 Uhr"
          />
        </div>

        <div className="mt-4 space-y-3">
          <Checkbox
            label="Einwilligung zur elektronischen Kommunikation"
            description="Ich stimme zu, dass die Bundesagentur für Arbeit mich per E-Mail kontaktieren darf"
            checked={formData?.emailConsent}
            onChange={(e) => onChange('emailConsent', e?.target?.checked)}
          />

          <Checkbox
            label="Einwilligung zur SMS-Benachrichtigung"
            description="Ich stimme zu, Terminerinnerungen und wichtige Updates per SMS zu erhalten"
            checked={formData?.smsConsent}
            onChange={(e) => onChange('smsConsent', e?.target?.checked)}
          />

          <Checkbox
            label="Einwilligung zur Datenverarbeitung"
            description="Ich stimme der Verarbeitung meiner Kontaktdaten gemäß DSGVO zu"
            checked={formData?.dataProcessingConsent}
            onChange={(e) => onChange('dataProcessingConsent', e?.target?.checked)}
            required
          />
        </div>
      </div>
      {/* Emergency Contact */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="AlertCircle" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Notfallkontakt</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Name"
            type="text"
            value={formData?.emergencyContactName}
            onChange={(e) => onChange('emergencyContactName', e?.target?.value)}
            placeholder="Vollständiger Name"
          />

          <Input
            label="Beziehung"
            type="text"
            value={formData?.emergencyContactRelation}
            onChange={(e) => onChange('emergencyContactRelation', e?.target?.value)}
            placeholder="z.B. Ehepartner, Elternteil"
          />

          <Input
            label="Telefonnummer"
            type="tel"
            value={formData?.emergencyContactPhone}
            onChange={(e) => onChange('emergencyContactPhone', e?.target?.value)}
            placeholder="+49 30 12345678"
          />
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsTab;